import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSave,
  faPlus,
  faTrash,
  faSync,
  faInfoCircle,
  faCheckCircle,
  faExclamationTriangle,
  faTable,
  faMapMarkerAlt,
  faGlobe,
  faTruck,
  faNairaSign
} from '@fortawesome/free-solid-svg-icons';
import { configService } from '../../../services/configService';
import { useAuth } from '../../../AuthContext';

const PricingConfig = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [monthlySubscriptionPriceUI, setMonthlySubscriptionPriceUI] = useState(3500);
  const [standardBaseFeeUI, setStandardBaseFeeUI] = useState(1500);
  const [ecommerceBaseFeeUI, setEcommerceBaseFeeUI] = useState(500);
  const [doorstepFeeUI, setDoorstepFeeUI] = useState(5000);
  const [ecommerceLocalBaseRateUI, setEcommerceLocalBaseRateUI] = useState(400);
  const [ecommerceLocalWeightBracketsUI, setEcommerceLocalWeightBracketsUI] = useState({ rate0_5: 0, rate5_10: 0, rate10Plus: 0 });
  const [lagosZonesUI, setLagosZonesUI] = useState([]);
  const [ecommerceLagosZonesUI, setEcommerceLagosZonesUI] = useState([]);
  const [interStateZonesUI, setInterStateZonesUI] = useState([]);
  const [interStateZoneRatesUI, setInterStateZoneRatesUI] = useState([]);
  const [ecommerceInterStateZoneRatesUI, setEcommerceInterStateZoneRatesUI] = useState([]);
  const [zoneMappingUI, setZoneMappingUI] = useState([]);
  const [internationalPricingTableUI, setInternationalPricingTableUI] = useState([]);

  const refreshFromServer = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await configService.getPricingConfig();
      const cfg = resp?.config || {};

      setMonthlySubscriptionPriceUI(Number(cfg.monthlySubscriptionPrice || 3500));
      setStandardBaseFeeUI(Number(cfg.standardBaseFee || 1500));
      setEcommerceBaseFeeUI(Number(cfg.ecommerceBaseFee || 500));
      setDoorstepFeeUI(Number(cfg.doorstepFee || 5000));
      setEcommerceLocalBaseRateUI(Number(cfg.ecommerceLocalBaseRate || 400));
      setEcommerceLocalWeightBracketsUI(cfg.ecommerceLocalWeightBrackets || { rate0_5: 0, rate5_10: 0, rate10Plus: 0 });

      const lagos = Array.isArray(cfg.lagosZones) ? cfg.lagosZones : [];
      setLagosZonesUI(
        lagos.map(z => ({
          name: z?.name || '',
          baseRate: Number(z?.baseRate || 0),
          areasText: Array.isArray(z?.areas) ? z.areas.join(', ') : '',
          specialAreasText: z?.specialAreas
            ? Object.entries(z.specialAreas).map(([k, v]) => `${k}:${v}`).join(', ')
            : '',
          weightBrackets: z?.weightBrackets || { rate0_5: 0, rate5_10: 0, rate10Plus: 0 }
        }))
      );

      const ecommerceLagos = Array.isArray(cfg.ecommerceLagosZones) ? cfg.ecommerceLagosZones : [];
      setEcommerceLagosZonesUI(
        ecommerceLagos.map(z => ({
          name: z?.name || '',
          baseRate: Number(z?.baseRate || 0),
          areasText: Array.isArray(z?.areas) ? z.areas.join(', ') : '',
          specialAreasText: z?.specialAreas
            ? Object.entries(z.specialAreas).map(([k, v]) => `${k}:${v}`).join(', ')
            : '',
          weightBrackets: z?.weightBrackets || { rate0_5: 0, rate5_10: 0, rate10Plus: 0 }
        }))
      );

      const interZones = cfg.interStateZones || {};
      setInterStateZonesUI(
        Object.entries(interZones).map(([zone, states]) => ({
          zone: String(zone),
          statesText: Array.isArray(states) ? states.join(', ') : '',
        }))
      );

      const interRates = cfg.interStateZoneRates || {};
      setInterStateZoneRatesUI(
        Object.entries(interRates).map(([zone, data]) => ({
          zone: String(zone),
          baseRate: Number(data?.baseRate || 0),
          rateAbove1kg: Number(data?.rateAbove1kg || 1000),
          weightBrackets: data?.weightBrackets || { rate0_5: 0, rate5_10: 0, rate10Plus: 0 }
        }))
      );

      const ecomInterRates = cfg.ecommerceInterStateZoneRates || {};
      setEcommerceInterStateZoneRatesUI(
        Object.entries(ecomInterRates).map(([zone, data]) => ({
          zone: String(zone),
          baseRate: Number(data?.baseRate || 0),
          rateAbove1kg: Number(data?.rateAbove1kg || 1000),
          weightBrackets: data?.weightBrackets || { rate0_5: 0, rate5_10: 0, rate10Plus: 0 }
        }))
      );

      const mapping = cfg.zoneMapping || {};
      setZoneMappingUI(
        Object.entries(mapping).map(([zone, countries]) => ({
          zone: String(zone),
          countriesText: Array.isArray(countries) ? countries.join(', ') : '',
        }))
      );

      const intlTable = Array.isArray(cfg.internationalPricingTable) ? cfg.internationalPricingTable : [];
      setInternationalPricingTableUI(intlTable);
    } catch (e) {
      setError('Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshFromServer();
  }, []);

  const handleSave = async () => {
    setError('Price updates are currently disabled to maintain consistency with the core logic.');
    return;
    // try {
    //   setSaving(true);
    setSuccess(null);
    setError(null);

    const lagosZones = lagosZonesUI.map(r => ({
      name: (r.name || '').trim(),
      baseRate: Number(r.baseRate || 0),
      areas: (r.areasText || '').split(',').map(s => s.trim()).filter(Boolean),
      specialAreas: parseSpecialAreas(r.specialAreasText || ''),
      weightBrackets: r.weightBrackets
    }));

    const ecommerceLagosZones = ecommerceLagosZonesUI.map(r => ({
      name: (r.name || '').trim(),
      baseRate: Number(r.baseRate || 0),
      areas: (r.areasText || '').split(',').map(s => s.trim()).filter(Boolean),
      specialAreas: parseSpecialAreas(r.specialAreasText || ''),
      weightBrackets: r.weightBrackets
    }));

    const interStateZones = {};
    interStateZonesUI.forEach(r => {
      const zoneKey = (r.zone || '').trim();
      if (!zoneKey) return;
      interStateZones[zoneKey] = (r.statesText || '').split(',').map(s => s.trim()).filter(Boolean);
    });

    const interStateZoneRates = {};
    interStateZoneRatesUI.forEach(r => {
      const zoneKey = (r.zone || '').trim();
      if (!zoneKey) return;
      interStateZoneRates[zoneKey] = {
        baseRate: Number(r.baseRate || 0),
        rateAbove1kg: Number(r.rateAbove1kg || 1000),
        weightBrackets: r.weightBrackets
      };
    });

    const ecommerceInterStateZoneRates = {};
    ecommerceInterStateZoneRatesUI.forEach(r => {
      const zoneKey = (r.zone || '').trim();
      if (!zoneKey) return;
      ecommerceInterStateZoneRates[zoneKey] = {
        baseRate: Number(r.baseRate || 0),
        rateAbove1kg: Number(r.rateAbove1kg || 1000),
        weightBrackets: r.weightBrackets
      };
    });

    const zoneMapping = {};
    zoneMappingUI.forEach(r => {
      const zoneKey = (r.zone || '').trim();
      if (!zoneKey) return;
      zoneMapping[zoneKey] = (r.countriesText || '').split(',').map(s => s.trim()).filter(Boolean);
    });

    const payload = {
      monthlySubscriptionPrice: Number(monthlySubscriptionPriceUI || 0),
      standardBaseFee: Number(standardBaseFeeUI || 0),
      ecommerceBaseFee: Number(ecommerceBaseFeeUI || 0),
      doorstepFee: Number(doorstepFeeUI || 0),
      ecommerceLocalBaseRate: Number(ecommerceLocalBaseRateUI || 0),
      ecommerceLocalWeightBrackets: ecommerceLocalWeightBracketsUI,
      lagosZones,
      ecommerceLagosZones,
      interStateZones,
      interStateZoneRates,
      ecommerceInterStateZoneRates,
      zoneMapping,
      internationalPricingTable: internationalPricingTableUI
    };
    await configService.updatePricingConfig(payload);
    await refreshFromServer();
    setSuccess('Pricing configuration saved successfully');
  }/* catch (e) {
    setError('Save failed. Please check your entries for errors.');
  } finally {
    setSaving(false);
  }
};)*/

const updateWeightBracket = (setter, list, index, bracket, value) => {
  const newList = [...list];
  const currentBrackets = newList[index].weightBrackets || { rate0_5: 0, rate5_10: 0, rate10Plus: 0 };
  newList[index] = {
    ...newList[index],
    weightBrackets: {
      ...currentBrackets,
      [bracket]: Number(value || 0)
    }
  };
  setter(newList);
};

const addLagosRow = () => setLagosZonesUI(prev => [...prev, { name: '', baseRate: 0, areasText: '', specialAreasText: '', weightBrackets: { rate0_5: 0, rate5_10: 0, rate10Plus: 0 } }]);
const removeLagosRow = idx => setLagosZonesUI(prev => prev.filter((_, i) => i !== idx));

const addEcommerceLagosRow = () => setEcommerceLagosZonesUI(prev => [...prev, { name: '', baseRate: 0, areasText: '', specialAreasText: '', weightBrackets: { rate0_5: 0, rate5_10: 0, rate10Plus: 0 } }]);
const removeEcommerceLagosRow = idx => setEcommerceLagosZonesUI(prev => prev.filter((_, i) => i !== idx));

const addInterZoneRow = () => setInterStateZonesUI(prev => [...prev, { zone: '', statesText: '' }]);
const removeInterZoneRow = idx => setInterStateZonesUI(prev => prev.filter((_, i) => i !== idx));

const addBaseRateRow = () => setInterStateZoneRatesUI(prev => [...prev, { zone: '', baseRate: 0, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 0, rate10Plus: 0 } }]);
const removeBaseRateRow = idx => setInterStateZoneRatesUI(prev => prev.filter((_, i) => i !== idx));

const addIntlRow = () => setZoneMappingUI(prev => [...prev, { zone: '', countriesText: '' }]);
const removeIntlRow = idx => setZoneMappingUI(prev => prev.filter((_, i) => i !== idx));

const addIntlRateRow = () => setInternationalPricingTableUI(prev => [...prev, { weight: 0, rates: {} }]);
const removeIntlRateRow = idx => setInternationalPricingTableUI(prev => prev.filter((_, i) => i !== idx));

const prefillFromDefaults = () => {
  const lagosDefaults = [
    {
      name: 'ZONE 1 - MAINLAND',
      baseRate: 2500,
      areasText: 'Surulere, Opebi, Anthony, Ejigbo, Isolo, Iyana Itire, Ikeja, Oshodi, Obanikoro, Mushin, Palmgrove, Onipanu, Ago palace, Ilupeju, Jakande gate, Gbagada, Jibowu, Fadeyi, Maryland, ojota, Festac, Amuwo odofin, Oworonshoki, Bariga, Yaba, Shomolu',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 2000, rate10Plus: 270 }
    },
    {
      name: 'ZONE 1 - ISLAND',
      baseRate: 3500,
      areasText: 'Ikoyi, Eko, Oniru, Admiralty Lekki, Victoria Island, Lagos Island, CMS, Idumota, Marwa Lekki, Lekki Phase 1, Lekki phase 2, Jakande lekki, Osapa London, Alpha beach Lekki, Orchid Rd Lekki, Ilasan Lekki, Chevron Lekki, VGC, Ikota Lekki, Agungi, Ologolo, Igbofon',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 2000, rate10Plus: 270 }
    },
    {
      name: 'ZONE 2 - MAINLAND',
      baseRate: 3000,
      areasText: 'Ikotun, Idimu, Ijegun, Isheri oshun, Costain, Egbeda, Gowon estate, Akowonjo, Cele egbe, Agege, Ketu, Alapere, Ebute metta, Ogba, Abule egba, Igando, Omole phase 1/2, Magodo, Ogudu, Iyana ipaja, Dopemu, Ifako ijaiye, Iju ishaga, Meiran, Alagbado, Ayobo, Alakuko',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 2000, rate10Plus: 325 }
    },
    {
      name: 'ZONE 2 - ISLAND',
      baseRate: 4000,
      areasText: 'Ajah, Abraham adensanya, Thomas estate, LBS, Lamgbasa, Badore ajah, Ogombo, Addo',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 2000, rate10Plus: 270 }
    },
    {
      name: 'ZONE 3 - MAINLAND',
      baseRate: 3500,
      areasText: 'Mile 2, Trade fair, Alaba, Maza Maza, Abule ado, Orile, Ojo, Iba, Satellite town, Navy town, Ajegunle, Apapa',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 2000, rate10Plus: 270 }
    },
    {
      name: 'ZONE 4 - MAINLAND',
      baseRate: 4000,
      areasText: 'Mowe, Opic, Ojodu Berger, Ikorodu, Ijainkin, Iyana era',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 2000, rate10Plus: 270 }
    },
    {
      name: 'ZONE 5 - ISLAND',
      baseRate: 4500,
      areasText: 'Sangotedo, Awoyaya, Lakowe',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 2000, rate10Plus: 270 }
    },
    {
      name: 'ZONE 6 - ISLAND',
      baseRate: 5500,
      areasText: 'Epe, Badagry, Ibeju Lekki',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 2000, rate10Plus: 270 }
    }
  ];
  setLagosZonesUI(lagosZones => lagosDefaults);

  const ecommerceLagosDefaults = [
    {
      name: 'ZONE 1 - MAINLAND',
      baseRate: 2000,
      areasText: 'Surulere, Opebi, Anthony, Ejigbo, Isolo, Iyana Itire, Ikeja, Oshodi, Obanikoro, Mushin, Palmgrove, Onipanu, Ago palace, Ilupeju, Jakande gate, Gbagada, Jibowu, Fadeyi, Maryland, ojota, Festac, Amuwo odofin, Oworonshoki, Bariga, Yaba, Shomolu',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 1500, rate10Plus: 100 }
    },
    {
      name: 'ZONE 1 - ISLAND',
      baseRate: 3000,
      areasText: 'Ikoyi, Eko, Oniru, Admiralty Lekki, Victoria Island, Lagos Island, CMS, Idumota, Marwa Lekki, Lekki Phase 1, Lekki phase 2, Jakande lekki, Osapa London, Alpha beach Lekki, Orchid Rd Lekki, Ilasan Lekki, Chevron Lekki, VGC, Ikota Lekki, Agungi, Ologolo, Igbofon',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 1500, rate10Plus: 100 }
    },
    {
      name: 'ZONE 2 - MAINLAND',
      baseRate: 2500,
      areasText: 'Ikotun, Idimu, Ijegun, Isheri oshun, Costain, Egbeda, Gowon estate, Akowonjo, Cele egbe, Agege, Ketu, Alapere, Ebute metta, Ogba, Abule egba, Igando, Omole phase 1/2, Magodo, Ogudu, Iyana ipaja, Dopemu, Ifako ijaiye, Iju ishaga, Meiran, Alagbado, Ayobo, Alakuko',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 1500, rate10Plus: 100 }
    },
    {
      name: 'ZONE 2 - ISLAND',
      baseRate: 3500,
      areasText: 'Ajah, Abraham adensanya, Thomas estate, LBS, Lamgbasa, Badore ajah, Ogombo, Addo',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 1500, rate10Plus: 100 }
    },
    {
      name: 'ZONE 3 - MAINLAND',
      baseRate: 3000,
      areasText: 'Mile 2, Trade fair, Alaba, Maza Maza, Abule ado, Orile, Ojo, Iba, Satellite town, Navy town, Ajegunle, Apapa',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 1500, rate10Plus: 100 }
    },
    {
      name: 'ZONE 4 - MAINLAND',
      baseRate: 3500,
      areasText: 'Mowe, Opic, Ojodu Berger, Ikorodu, Ijainkin, Iyana era',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 1500, rate10Plus: 100 }
    },
    {
      name: 'ZONE 5 - ISLAND',
      baseRate: 4000,
      areasText: 'Sangotedo, Awoyaya, Lakowe',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 1500, rate10Plus: 100 }
    },
    {
      name: 'ZONE 6 - ISLAND',
      baseRate: 5000,
      areasText: 'Epe, Badagry, Ibeju Lekki',
      specialAreasText: '',
      weightBrackets: { rate0_5: 0, rate5_10: 1500, rate10Plus: 100 }
    }
  ];
  setEcommerceLagosZonesUI(ecommerceLagosDefaults);

  const interStateDefaults = {
    1: ['Abeokuta', 'Ado Ekiti', 'Osogbo', 'Ibadan', 'Iwo road', 'Challenge', 'Ring road', 'Ilorin', 'Akure', 'Ogun', 'Ekiti', 'Osun', 'Oyo', 'Kwara', 'Ondo'],
    2: ['Aba', 'Owerri', 'Enugu', 'Onitsha', 'Awka', 'Abia', 'Imo', 'Anambra'],
    3: ['Asaba', 'Warri', 'Benin', 'Benin city', 'Delta', 'Edo'],
    4: ['Abuja', 'FCT', 'Uyo', 'Port Harcourt', 'Bayelsa', 'Akwa Ibom', 'Rivers'],
    5: ['Kano', 'Kaduna', 'Makurdi', 'Minna', 'Bauchi', 'Benue', 'Niger']
  };
  setInterStateZonesUI(Object.entries(interStateDefaults).map(([zone, states]) => ({ zone, statesText: states.join(', ') })));

  const interStateRateDefaults = {
    1: { baseRate: 5074, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 1476, rate10Plus: 700 } },
    2: { baseRate: 6574, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 1376, rate10Plus: 770 } },
    3: { baseRate: 5674, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 1876, rate10Plus: 870 } },
    4: { baseRate: 6674, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 876, rate10Plus: 870 } },
    5: { baseRate: 9674, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 876, rate10Plus: 1170 } }
  };
  setInterStateZoneRatesUI(Object.entries(interStateRateDefaults).map(([zone, data]) => ({ zone, ...data })));

  const ecommerceInterStateRateDefaults = {
    1: { baseRate: 4574, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 1776, rate10Plus: 500 } },
    2: { baseRate: 5674, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 1876, rate10Plus: 650 } },
    3: { baseRate: 5174, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 1376, rate10Plus: 730 } },
    4: { baseRate: 4975, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 1575, rate10Plus: 730 } },
    5: { baseRate: 7375, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 2175, rate10Plus: 1030 } }
  };
  setEcommerceInterStateZoneRatesUI(Object.entries(ecommerceInterStateRateDefaults).map(([zone, data]) => ({ zone, ...data })));

  const intlMappingDefaults = {
    1: ["UNITED KINGDOM", "IRELAND", "DUBLIN", "SCOTLAND"],
    2: ["GHANA", "SENEGAL", "GAMBIA"],
    3: ["USA", "CANADA", "MEXICO"],
    4: ["CYPRUS", "GERMANY", "FRANCE", "ITALY", "MALTA", "PORTUGAL", "SPAIN", "SWEDEN", "TURKEY", "AUSTRIA"],
    5: ["ANGOLA", "ETHIOPIA", "KENYA", "LIBYA", "NAMIBIA", "SOUTH AFRICA", "SUDAN", "TANZANIA", "ZAMBIA"],
    6: ["IRAQ", "ISRAEL", "JORDAN", "KUWAIT", "QATAR", "SAUDI ARABIA", "UNITED ARAB EMIRATES", "UAE", "OMAN"],
    7: ["CHINA", "INDIA", "JAPAN", "KOREA", "MALAYSIA", "PAKISTAN", "PHILIPPINES", "SINGAPORE", "VIETNAM", "MALDIVES"],
    8: ["ARGENTINA", "COLOMBIA", "BARBADOS", "BRAZIL", "FRENCH GUYANA", "NEW ZEALAND", "JAMAICA"]
  };
  setZoneMappingUI(Object.entries(intlMappingDefaults).map(([zone, countries]) => ({ zone, countriesText: countries.join(', ') })));

  const intlRateDefaults = [
    { weight: 0.5, rates: { "1": 63300.04, "2": 69945.26, "3": 76906.88, "4": 88931.58, "5": 91463.10, "6": 98424.70, "7": 107917.88, "8": 112348.00 } },
    { weight: 1.0, rates: { "1": 64218.80, "2": 70864.02, "3": 77825.64, "4": 89850.33, "5": 92381.85, "6": 99343.46, "7": 108836.64, "8": 113266.76 } },
    { weight: 1.5, rates: { "1": 65424.74, "2": 72069.97, "3": 79031.59, "4": 91056.27, "5": 93587.79, "6": 100549.41, "7": 110042.59, "8": 114472.71 } },
    { weight: 2.0, rates: { "1": 66630.69, "2": 73275.92, "3": 80237.53, "4": 92262.22, "5": 94793.74, "6": 101755.36, "7": 111248.53, "8": 115678.65 } },
    { weight: 2.5, rates: { "1": 93142.45, "2": 100880.02, "3": 116355.02, "4": 129250.90, "5": 131830.00, "6": 140857.10, "7": 151173.88, "8": 165359.37 } },
    { weight: 3.0, rates: { "1": 98442.14, "2": 106179.72, "3": 129578.62, "4": 135369.31, "5": 138767.16, "6": 147794.25, "7": 158929.80, "8": 173934.02 } },
    { weight: 3.5, rates: { "1": 103741.83, "2": 111479.40, "3": 142802.23, "4": 141487.70, "5": 145704.28, "6": 154731.38, "7": 166685.70, "8": 182508.67 } },
    { weight: 4.0, rates: { "1": 109041.51, "2": 116779.08, "3": 156025.82, "4": 147606.10, "5": 152641.44, "6": 161668.53, "7": 174441.60, "8": 191083.30 } },
    { weight: 4.5, rates: { "1": 114341.20, "2": 122078.77, "3": 169249.42, "4": 153724.51, "5": 159578.58, "6": 168605.67, "7": 182197.50, "8": 199657.96 } },
    { weight: 5.0, rates: { "1": 119640.90, "2": 127378.46, "3": 182473.02, "4": 159842.89, "5": 166515.72, "6": 175542.81, "7": 189953.41, "8": 208232.61 } },
    { weight: 6.0, rates: { "1": 157106.25, "2": 166738.56, "3": 235289.74, "4": 208859.55, "5": 218473.99, "6": 230376.99, "7": 249224.50, "8": 278078.53 } },
    { weight: 7.0, rates: { "1": 179614.26, "2": 190045.20, "3": 264247.90, "4": 237223.64, "5": 248834.32, "6": 262334.36, "7": 283577.30, "8": 320416.52 } },
    { weight: 8.0, rates: { "1": 202122.28, "2": 213351.82, "3": 293206.09, "4": 265587.68, "5": 279194.62, "6": 294291.75, "7": 317930.10, "8": 362754.54 } },
    { weight: 9.0, rates: { "1": 224630.30, "2": 236658.46, "3": 322164.26, "4": 293951.72, "5": 309554.96, "6": 326249.13, "7": 352282.88, "8": 405092.50 } },
    { weight: 10.0, rates: { "1": 247138.30, "2": 259965.07, "3": 351122.43, "4": 322315.78, "5": 339915.26, "6": 358206.51, "7": 386635.69, "8": 447430.51 } },
    { weight: 11.0, rates: { "1": 268075.88, "2": 281648.01, "3": 375220.12, "4": 348576.91, "5": 367986.33, "6": 387768.16, "7": 418433.19, "8": 486574.47 } },
    { weight: 12.0, rates: { "1": 289013.46, "2": 303330.93, "3": 399317.82, "4": 374838.04, "5": 396057.42, "6": 417329.83, "7": 450230.70, "8": 525718.45 } },
    { weight: 13.0, rates: { "1": 309951.04, "2": 325013.86, "3": 423415.52, "4": 401099.18, "5": 424128.49, "6": 446891.48, "7": 482028.21, "8": 564862.40 } },
    { weight: 14.0, rates: { "1": 330888.62, "2": 346696.80, "3": 447513.21, "4": 427360.30, "5": 452199.56, "6": 476453.14, "7": 513825.72, "8": 604006.38 } },
    { weight: 15.0, rates: { "1": 351826.20, "2": 368379.72, "3": 471610.90, "4": 453621.44, "5": 480270.64, "6": 506014.82, "7": 545623.22, "8": 643150.34 } },
    { weight: 16.0, rates: { "1": 372763.77, "2": 390062.65, "3": 495708.60, "4": 479882.56, "5": 508341.72, "6": 535576.47, "7": 577420.72, "8": 682294.30 } },
    { weight: 17.0, rates: { "1": 393701.35, "2": 411745.58, "3": 519806.31, "4": 506143.70, "5": 536412.80, "6": 565138.14, "7": 609218.23, "8": 721438.27 } },
    { weight: 18.0, rates: { "1": 414638.92, "2": 433428.51, "3": 543904.00, "4": 532404.84, "5": 564483.87, "6": 594699.79, "7": 641015.74, "8": 760582.24 } },
    { weight: 19.0, rates: { "1": 435576.50, "2": 455111.44, "3": 568001.70, "4": 558665.97, "5": 592554.96, "6": 624261.45, "7": 672813.25, "8": 799726.20 } },
    { weight: 20.0, rates: { "1": 456514.08, "2": 476794.38, "3": 592099.39, "4": 584927.10, "5": 620626.03, "6": 653823.10, "7": 704610.75, "8": 838870.17 } },
    { weight: 21.0, rates: { "1": 522181.92, "2": 540962.73, "3": 592610.94, "4": 658345.65, "5": 705297.36, "6": 740512.41, "7": 796858.21, "8": 984670.15 } },
    { weight: 22.0, rates: { "1": 546952.47, "2": 566627.62, "3": 620735.26, "4": 689600.20, "5": 738787.71, "6": 775679.67, "7": 834708.60, "8": 1013463.97 } },
    { weight: 23.0, rates: { "1": 571723.04, "2": 592292.52, "3": 648859.58, "4": 720854.77, "5": 772278.06, "6": 810846.94, "7": 872558.98, "8": 1078257.78 } },
    { weight: 24.0, rates: { "1": 596493.61, "2": 617957.42, "3": 676983.92, "4": 752109.33, "5": 805768.41, "6": 846014.19, "7": 910409.38, "8": 1125051.60 } },
    { weight: 25.0, rates: { "1": 621264.18, "2": 643622.31, "3": 705108.25, "4": 783363.88, "5": 839258.76, "6": 881181.45, "7": 948259.77, "8": 1171845.42 } }
  ];
  setInternationalPricingTableUI(intlRateDefaults);
};

const prefillAllNigerianStates = () => {
  const allStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'Abuja', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
    'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
    'Taraba', 'Yobe', 'Zamfara'
  ];
  setInterStateZonesUI([{ zone: '1', statesText: allStates.join(', ') }]);
};

const parseSpecialAreas = (text) => {
  const obj = {};
  if (!text) return obj;
  text.split(',').map(s => s.trim()).filter(Boolean).forEach(pair => {
    const [k, v] = pair.split(':').map(x => x.trim());
    const num = Number(v);
    if (k && !Number.isNaN(num)) obj[k] = num;
  });
  return obj;
};

const WeightBracketInputs = ({ list, index, setter }) => {
  const brackets = list[index]?.weightBrackets || { rate0_5: 0, rate5_10: 0, rate10Plus: 0 };
  return (
    <div className="flex flex-col space-y-2 py-1">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center space-x-1">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">0-5kg:</span>
          <input
            type="number"
            className="w-20 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold text-gray-900 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            value={brackets.rate0_5}
            onChange={e => updateWeightBracket(setter, list, index, 'rate0_5', e.target.value)}
          />
        </div>
        <div className="flex items-center space-x-1">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">5-10kg:</span>
          <input
            type="number"
            className="w-20 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold text-gray-900 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            value={brackets.rate5_10}
            onChange={e => updateWeightBracket(setter, list, index, 'rate5_10', e.target.value)}
          />
        </div>
        <div className="flex items-center space-x-1">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">10kg+:</span>
          <input
            type="number"
            className="w-20 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold text-gray-900 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            value={brackets.rate10Plus}
            onChange={e => updateWeightBracket(setter, list, index, 'rate10Plus', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ icon, title, description, color, onSave, saving, onAdd, addText }) => (
  <div className="p-4 sm:p-6 border-b border-gray-100">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-center">
        <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center mr-3 shrink-0`}>
          <FontAwesomeIcon icon={icon} className={`text-${color}-600`} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          {description && <p className="text-xs sm:text-sm text-gray-500">{description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-[52px] sm:ml-0">
        {onAdd && (
          <button
            onClick={onAdd}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center text-xs font-medium transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
            {addText || 'Add'}
          </button>
        )}
        <button
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center text-xs font-bold shadow-sm transition-all active:scale-95 disabled:opacity-60"
        >
          {saving ? (
            <FontAwesomeIcon icon={faSync} className="animate-spin" />
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} className="mr-1.5" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  </div>
);

if (!isAdmin) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-2xl" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
        <p className="text-gray-600 mb-6">Admin access required to view this page.</p>
        <button
          onClick={() => navigate('/dashboard/admin')}
          className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
        >
          Back to Admin Dashboard
        </button>
      </div>
    </div>
  );
}

return (
  <div className="min-h-screen bg-gray-50">
    {/* Header */}
    <div className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/dashboard/admin')}
              className="mr-4 w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Pricing Settings</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={prefillFromDefaults}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 flex items-center transition-colors"
            >
              <FontAwesomeIcon icon={faTable} className="mr-2" />
              Prefill Defaults
            </button>
            <button
              onClick={prefillAllNigerianStates}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 flex items-center transition-colors"
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
              All Nigerian States
            </button>
            <button
              onClick={refreshFromServer}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 flex items-center transition-colors disabled:opacity-60"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSync} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium">Loading pricing configuration...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Global Base Fees */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <SectionHeader
              icon={faNairaSign}
              title="Global Pricing Settings"
              description="Configure subscription and base fees for different user types."
              color="red"
              onSave={handleSave}
              saving={saving}
            />
            <div className="p-4 sm:p-6">
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Setting</th>
                      <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Amount (₦)</th>
                      <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Details</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-4 font-medium text-gray-800 text-xs sm:text-sm">Monthly Subscription</td>
                      <td className="px-4 py-4 w-32 sm:w-48">
                        <input
                          type="number"
                          value={monthlySubscriptionPriceUI}
                          onChange={e => setMonthlySubscriptionPriceUI(Number(e.target.value || 0))}
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-lg border-gray-300 focus:ring-red-500 text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 hidden sm:table-cell">Amount an ecommerce customer pays monthly to keep access active.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-4 font-medium text-gray-800 text-xs sm:text-sm">Standard User Base Fee</td>
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          value={standardBaseFeeUI}
                          onChange={e => setStandardBaseFeeUI(Number(e.target.value || 0))}
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-lg border-gray-300 focus:ring-red-500 text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 hidden sm:table-cell">General extra charge added to normal users before shipment totals are shown.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-4 font-medium text-gray-800 text-xs sm:text-sm">Ecommerce User Base Fee</td>
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          value={ecommerceBaseFeeUI}
                          onChange={e => setEcommerceBaseFeeUI(Number(e.target.value || 0))}
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-lg border-gray-300 focus:ring-red-500 text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 hidden sm:table-cell">General extra charge added to ecommerce bookings before final totals are shown.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-4 font-medium text-gray-800 text-xs sm:text-sm">Doorstep Delivery Fee</td>
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          value={doorstepFeeUI}
                          onChange={e => setDoorstepFeeUI(Number(e.target.value || 0))}
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-lg border-gray-300 focus:ring-red-500 text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 hidden sm:table-cell">Extra charge added when 'Doorstep Delivery' is selected (applies to Local and Interstate).</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="px-4 py-4 font-medium text-gray-800 text-xs sm:text-sm">Ecommerce Lagos Fallback</td>
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          value={ecommerceLocalBaseRateUI}
                          onChange={e => setEcommerceLocalBaseRateUI(Number(e.target.value || 0))}
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-lg border-gray-300 focus:ring-red-500 text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 hidden sm:table-cell">Used only when a Lagos ecommerce location is not found in the table below.</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="px-4 py-4 font-medium text-gray-800 text-xs sm:text-sm">Fallback Weight 0-5kg</td>
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          value={ecommerceLocalWeightBracketsUI.rate0_5}
                          onChange={e => setEcommerceLocalWeightBracketsUI(prev => ({ ...prev, rate0_5: Number(e.target.value || 0) }))}
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-lg border-gray-300 focus:ring-red-500 text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 hidden sm:table-cell">Extra amount per kg if the booking falls back to the default Lagos ecommerce rule.</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="px-4 py-4 font-medium text-gray-800 text-xs sm:text-sm">Fallback Weight 5-10kg</td>
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          value={ecommerceLocalWeightBracketsUI.rate5_10}
                          onChange={e => setEcommerceLocalWeightBracketsUI(prev => ({ ...prev, rate5_10: Number(e.target.value || 0) }))}
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-lg border-gray-300 focus:ring-red-500 text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 hidden sm:table-cell">Extra amount per kg for fallback Lagos ecommerce bookings between 5kg and 10kg.</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="px-4 py-4 font-medium text-gray-800 text-xs sm:text-sm">Fallback Weight 10kg+</td>
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          value={ecommerceLocalWeightBracketsUI.rate10Plus}
                          onChange={e => setEcommerceLocalWeightBracketsUI(prev => ({ ...prev, rate10Plus: Number(e.target.value || 0) }))}
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-lg border-gray-300 focus:ring-red-500 text-sm"
                        />
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 hidden sm:table-cell">Extra amount per kg for fallback Lagos ecommerce bookings above 10kg.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-[10px] sm:text-xs text-gray-500">Tip: Most Lagos ecommerce prices should be updated in the "Lagos Ecommerce Zones" table below. The fallback rows above are only backup values.</p>
            </div>
          </div>
          {error && (
            <div className="mb-6 animate-fade-in">
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 text-red-500" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 animate-fade-in">
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-3 text-green-500" />
                <span>{success}</span>
              </div>
            </div>
          )}

          {/* Lagos Zones Table */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <SectionHeader
              icon={faMapMarkerAlt}
              title="Lagos Standard Zones"
              color="blue"
              onSave={handleSave}
              saving={saving}
              onAdd={addLagosRow}
              addText="Add Zone"
            />
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Zone</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Base Rate</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Tiers (₦/kg)</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Areas</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Special Fees</th>
                    <th className="px-4 py-3 text-right text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lagosZonesUI.map((row, idx) => (
                    <tr key={`lagos-${idx}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <input
                          className="w-24 sm:w-full px-2 py-1.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.name}
                          onChange={e => {
                            const v = e.target.value;
                            setLagosZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, name: v } : r));
                          }}
                          placeholder="Zone"
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 sm:pl-4 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faNairaSign} className="text-gray-400 text-xs sm:text-sm" />
                          </div>
                          <input
                            type="number"
                            className="w-20 sm:w-full pl-6 sm:pl-10 pr-2 py-1.5 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                            value={row.baseRate}
                            onChange={e => {
                              const v = e.target.value;
                              setLagosZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, baseRate: Number(v) } : r));
                            }}
                            placeholder="0"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:py-6">
                        <WeightBracketInputs list={lagosZonesUI} index={idx} setter={setLagosZonesUI} />
                      </td>
                      <td className="px-4 py-4 sm:py-6 min-w-[150px] sm:min-w-0">
                        <textarea
                          rows="1"
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.areasText}
                          onChange={e => {
                            const v = e.target.value;
                            setLagosZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, areasText: v } : r));
                          }}
                          placeholder="Area1, Area2..."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 min-w-[120px] sm:min-w-0">
                        <input
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.specialAreasText}
                          onChange={e => {
                            const v = e.target.value;
                            setLagosZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, specialAreasText: v } : r));
                          }}
                          placeholder="Area:Fee..."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap text-right">
                        <button
                          onClick={() => removeLagosRow(idx)}
                          className="p-1.5 sm:px-3 sm:py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs sm:text-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Lagos Ecommerce Zones Table */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <SectionHeader
              icon={faMapMarkerAlt}
              title="Lagos Ecommerce Zones"
              color="pink"
              onSave={handleSave}
              saving={saving}
              onAdd={addEcommerceLagosRow}
              addText="Add Ecommerce Zone"
            />
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Zone</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Base Rate</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Tiers (₦/kg)</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Areas</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Special Fees</th>
                    <th className="px-4 py-3 text-right text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ecommerceLagosZonesUI.map((row, idx) => (
                    <tr key={`ecommerce-lagos-${idx}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <input
                          className="w-24 sm:w-full px-2 py-1.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.name}
                          onChange={e => {
                            const v = e.target.value;
                            setEcommerceLagosZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, name: v } : r));
                          }}
                          placeholder="Zone"
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 sm:pl-4 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faNairaSign} className="text-gray-400 text-xs sm:text-sm" />
                          </div>
                          <input
                            type="number"
                            className="w-20 sm:w-full pl-6 sm:pl-10 pr-2 py-1.5 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                            value={row.baseRate}
                            onChange={e => {
                              const v = e.target.value;
                              setEcommerceLagosZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, baseRate: Number(v) } : r));
                            }}
                            placeholder="0"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:py-6">
                        <WeightBracketInputs list={ecommerceLagosZonesUI} index={idx} setter={setEcommerceLagosZonesUI} />
                      </td>
                      <td className="px-4 py-4 sm:py-6 min-w-[150px] sm:min-w-0">
                        <textarea
                          rows="1"
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.areasText}
                          onChange={e => {
                            const v = e.target.value;
                            setEcommerceLagosZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, areasText: v } : r));
                          }}
                          placeholder="Area1, Area2..."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 min-w-[120px] sm:min-w-0">
                        <input
                          className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.specialAreasText}
                          onChange={e => {
                            const v = e.target.value;
                            setEcommerceLagosZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, specialAreasText: v } : r));
                          }}
                          placeholder="Area:Fee..."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap text-right">
                        <button
                          onClick={() => removeEcommerceLagosRow(idx)}
                          className="p-1.5 sm:px-3 sm:py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs sm:text-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Inter-state Zones Table */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <SectionHeader
              icon={faTruck}
              title="Interstate Zones"
              color="green"
              onSave={handleSave}
              saving={saving}
              onAdd={addInterZoneRow}
              addText="Add Zone"
            />
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Zone</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">States</th>
                    <th className="px-4 py-3 text-right text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {interStateZonesUI.map((row, idx) => (
                    <tr key={`interzone-${idx}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <input
                          className="w-20 sm:w-full px-2 py-1.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.zone}
                          onChange={e => {
                            const v = e.target.value;
                            setInterStateZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, zone: v } : r));
                          }}
                          placeholder="No."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 min-w-[200px] sm:min-w-0">
                        <input
                          className="w-full px-2 py-1.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.statesText}
                          onChange={e => {
                            const v = e.target.value;
                            setInterStateZonesUI(prev => prev.map((r, i) => i === idx ? { ...r, statesText: v } : r));
                          }}
                          placeholder="State1, State2..."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap text-right">
                        <button
                          onClick={() => removeInterZoneRow(idx)}
                          className="p-1.5 sm:px-3 sm:py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs sm:text-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Inter-state Base Rates Table */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <SectionHeader
              icon={faNairaSign}
              title="Interstate Base Rates"
              color="purple"
              onSave={handleSave}
              saving={saving}
              onAdd={addBaseRateRow}
              addText="Add Rate"
            />
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Zone</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Base (₦)</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Add. 1kg</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Tiers (₦/kg)</th>
                    <th className="px-4 py-3 text-right text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {interStateZoneRatesUI.map((row, idx) => (
                    <tr key={`baserate-${idx}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <input
                          className="w-16 sm:w-full px-2 py-1.5 sm:px-4 sm:py-3 font-bold border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.zone}
                          onChange={e => {
                            const v = e.target.value;
                            setInterStateZoneRatesUI(prev => prev.map((r, i) => i === idx ? { ...r, zone: v } : r));
                          }}
                          placeholder="No."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 sm:pl-4 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faNairaSign} className="text-gray-400 text-xs sm:text-sm" />
                          </div>
                          <input
                            type="number"
                            className="w-24 sm:w-full pl-6 sm:pl-10 pr-2 py-1.5 sm:py-3 font-bold border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                            value={row.baseRate}
                            onChange={e => {
                              const v = e.target.value;
                              setInterStateZoneRatesUI(prev => prev.map((r, i) => i === idx ? { ...r, baseRate: Number(v) } : r));
                            }}
                            placeholder="0"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 sm:pl-4 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faNairaSign} className="text-gray-400 text-xs sm:text-sm" />
                          </div>
                          <input
                            type="number"
                            className="w-24 sm:w-full pl-6 sm:pl-10 pr-2 py-1.5 sm:py-3 font-bold border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                            value={row.rateAbove1kg}
                            onChange={e => {
                              const v = e.target.value;
                              setInterStateZoneRatesUI(prev => prev.map((r, i) => i === idx ? { ...r, rateAbove1kg: Number(v) } : r));
                            }}
                            placeholder="1000"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:py-6">
                        <WeightBracketInputs list={interStateZoneRatesUI} index={idx} setter={setInterStateZoneRatesUI} />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap text-right">
                        <button
                          onClick={() => removeBaseRateRow(idx)}
                          className="p-1.5 sm:px-3 sm:py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs sm:text-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Interstate Ecommerce Base Rates Table */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <SectionHeader
              icon={faNairaSign}
              title="Interstate Ecommerce Base Rates"
              color="pink"
              onSave={handleSave}
              saving={saving}
              onAdd={() => setEcommerceInterStateZoneRatesUI(prev => [...prev, { zone: '', baseRate: 0, rateAbove1kg: 1000, weightBrackets: { rate0_5: 0, rate5_10: 0, rate10Plus: 0 } }])}
              addText="Add Rate"
            />
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Zone</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Base (₦)</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Add. 1kg</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Tiers (₦/kg)</th>
                    <th className="px-4 py-3 text-right text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ecommerceInterStateZoneRatesUI.map((row, idx) => (
                    <tr key={`ecom-baserate-${idx}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <input
                          className="w-16 sm:w-full px-2 py-1.5 sm:px-4 sm:py-3 font-bold border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.zone}
                          onChange={e => {
                            const v = e.target.value;
                            setEcommerceInterStateZoneRatesUI(prev => prev.map((r, i) => i === idx ? { ...r, zone: v } : r));
                          }}
                          placeholder="No."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 sm:pl-4 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faNairaSign} className="text-gray-400 text-xs sm:text-sm" />
                          </div>
                          <input
                            type="number"
                            className="w-24 sm:w-full pl-6 sm:pl-10 pr-2 py-1.5 sm:py-3 font-bold border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                            value={row.baseRate}
                            onChange={e => {
                              const v = e.target.value;
                              setEcommerceInterStateZoneRatesUI(prev => prev.map((r, i) => i === idx ? { ...r, baseRate: Number(v) } : r));
                            }}
                            placeholder="0"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 sm:pl-4 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faNairaSign} className="text-gray-400 text-xs sm:text-sm" />
                          </div>
                          <input
                            type="number"
                            className="w-24 sm:w-full pl-6 sm:pl-10 pr-2 py-1.5 sm:py-3 font-bold border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                            value={row.rateAbove1kg}
                            onChange={e => {
                              const v = e.target.value;
                              setEcommerceInterStateZoneRatesUI(prev => prev.map((r, i) => i === idx ? { ...r, rateAbove1kg: Number(v) } : r));
                            }}
                            placeholder="1000"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:py-6">
                        <WeightBracketInputs list={ecommerceInterStateZoneRatesUI} index={idx} setter={setEcommerceInterStateZoneRatesUI} />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap text-right">
                        <button
                          onClick={() => setEcommerceInterStateZoneRatesUI(prev => prev.filter((_, i) => i !== idx))}
                          className="p-1.5 sm:px-3 sm:py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs sm:text-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* International Zone Mapping Table */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <SectionHeader
              icon={faGlobe}
              title="International Zone Mapping"
              color="yellow"
              onSave={handleSave}
              saving={saving}
              onAdd={addIntlRow}
              addText="Add Zone"
            />
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Zone</th>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Countries</th>
                    <th className="px-4 py-3 text-right text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {zoneMappingUI.map((row, idx) => (
                    <tr key={`intl-mapping-${idx}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap">
                        <input
                          className="w-20 sm:w-full px-2 py-1.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.zone}
                          onChange={e => {
                            const v = e.target.value;
                            setZoneMappingUI(prev => prev.map((r, i) => i === idx ? { ...r, zone: v } : r));
                          }}
                          placeholder="No."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6">
                        <textarea
                          rows="1"
                          className="w-full px-2 py-1.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm transition-colors"
                          value={row.countriesText}
                          onChange={e => {
                            const v = e.target.value;
                            setZoneMappingUI(prev => prev.map((r, i) => i === idx ? { ...r, countriesText: v } : r));
                          }}
                          placeholder="Country1, Country2, ..."
                        />
                      </td>
                      <td className="px-4 py-4 sm:py-6 whitespace-nowrap text-right">
                        <button
                          onClick={() => removeIntlRow(idx)}
                          className="p-1.5 sm:px-3 sm:py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs sm:text-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* International Zone Rates Table */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <SectionHeader
              icon={faNairaSign}
              title="International Pricing Table (Weight-Based)"
              color="orange"
              onSave={handleSave}
              saving={saving}
              onAdd={addIntlRateRow}
              addText="Add Weight Row"
            />
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">Weight (KG)</th>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(z => (
                      <th key={z} className="px-4 py-3 text-left text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Z{z}</th>
                    ))}
                    <th className="px-4 py-3 text-right text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {internationalPricingTableUI.map((row, idx) => (
                    <tr key={`intl-weight-row-${idx}`} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-4 py-4 whitespace-nowrap sticky left-0 bg-white group-hover:bg-gray-50 z-10 border-r border-gray-100 shadow-[2px_0_5px_rgba(0,0,0,0.05)]">
                        <input
                          type="number"
                          step="0.1"
                          className="w-16 sm:w-20 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm font-bold"
                          value={row.weight}
                          onChange={e => {
                            const v = Number(e.target.value);
                            setInternationalPricingTableUI(prev => prev.map((r, i) => i === idx ? { ...r, weight: v } : r));
                          }}
                        />
                      </td>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(z => (
                        <td key={z} className="px-2 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            className="w-24 sm:w-28 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-xs sm:text-sm"
                            value={row.rates?.[String(z)] || 0}
                            onChange={e => {
                              const v = Number(e.target.value);
                              setInternationalPricingTableUI(prev => prev.map((r, i) => {
                                if (i === idx) {
                                  const newRates = { ...(r.rates || {}) };
                                  newRates[String(z)] = v;
                                  return { ...r, rates: newRates };
                                }
                                return r;
                              }));
                            }}
                          />
                        </td>
                      ))}
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => removeIntlRateRow(idx)}
                          className="p-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="pb-12"></div>
        </div>
      )}
    </div>
  </div>
);
};

export default PricingConfig;
